import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser } from '../api/auth';
import { checkSession, loginRequest, logoutRequest, registerRequest } from '../api/auth';
import { clearStoredPlaylistSelection, dispatchPlaylistStorageSync } from '../lib/playlistStorage';

const USER_KEY = 'user';
const TOKEN_KEY = 'authToken';

type AuthState = {
  user: AuthUser | null;
  token: string | null;
  ready: boolean;
};

type AuthContextValue = AuthState & {
  login: (u: string, p: string, remember?: boolean) => Promise<{ ok: boolean; message?: string }>;
  register: (payload: {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
  }) => Promise<{ ok: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => readStoredUser());
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [ready, setReady] = useState(false);

  const persist = useCallback((t: string | null, u: AuthUser | null) => {
    setToken(t);
    setUser(u);
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
    clearStoredPlaylistSelection();
    dispatchPlaylistStorageSync();
  }, []);

  const refreshUser = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      persist(null, null);
      return;
    }
    const data = await checkSession();
    if (!data.authenticated) {
      persist(null, null);
      return;
    }
    if (data.user) {
      setUser(data.user as AuthUser);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    }
  }, [persist]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refreshUser();
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  const login = useCallback(
    async (username: string, password: string, remember = false) => {
      const res = await loginRequest(username, password, remember);
      if (res.success) {
        persist(res.token, res.user);
        return { ok: true as const };
      }
      return { ok: false as const, message: res.message || 'Invalid credentials' };
    },
    [persist],
  );

  const register = useCallback(
    async (payload: {
      username: string;
      email: string;
      password: string;
      confirmPassword: string;
    }) => {
      const res = await registerRequest(payload);
      if (res.success) {
        persist(res.token, res.user);
        return { ok: true as const };
      }
      return { ok: false as const, message: res.message || 'Registration failed' };
    },
    [persist],
  );

  const logout = useCallback(async () => {
    await logoutRequest();
    persist(null, null);
  }, [persist]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      ready,
      login,
      register,
      logout,
      refreshUser,
    }),
    [user, token, ready, login, register, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
